/**
 * Permission Types and Centralized Permission Matrix
 *
 * This file defines all role-based access control types and the centralized
 * permission matrix that governs what each role can do across the application.
 *
 * IMPORTANT: This must stay in sync with backend ActionPolicy policies.
 */

export type UserRole = 'admin' | 'sales' | 'deliverer' | 'accountant'

export type ResourceType =
  | 'customers'
  | 'products'
  | 'product_items'
  | 'sales'
  | 'orders'
  | 'deliveries'
  | 'payments'
  | 'arrivals'
  | 'collaborators'
  | 'collaborations'
  | 'shippers'
  | 'shipments'
  | 'delivery_zones'
  | 'locations'
  | 'analytics'
  | 'settings'
  | 'users'
  | 'sale_runner_ups'

export type ActionType =
  | 'view' // Can view/list resources
  | 'view_all' // Can view all resources (not just assigned)
  | 'create' // Can create new resources
  | 'edit' // Can edit existing resources
  | 'delete' // Can delete resources
  | 'manage' // Full management access
  | 'assign' // Can assign deliveries/resources
  | 'adjust_balance' // Can adjust customer balance (admin only)
  | 'view_financials' // Can view financial data
  | 'confirm' // Can confirm payments/orders
  | 'allocate' // Can allocate payments
  | 'receive' // Can receive arrivals
  | 'verify' // Can verify arrivals
  | 'close' // Can close arrivals
  | 'refresh_cache' // Can refresh analytics cache

export interface PermissionCheck {
  action: ActionType
  resource: ResourceType
  hide?: boolean // If true, hide element entirely. If false, just disable it.
}

/**
 * Centralized Permission Matrix
 *
 * Maps each resource → action → allowed roles
 * This is the single source of truth for permissions.
 */
export const PERMISSION_MATRIX: Record<ResourceType, Partial<Record<ActionType, UserRole[]>>> = {
  customers: {
    view: ['admin', 'sales', 'deliverer', 'accountant'],
    view_all: ['admin', 'sales', 'accountant'],
    create: ['admin', 'sales'],
    edit: ['admin', 'sales'],
    delete: ['admin'],
    adjust_balance: ['admin'],
    view_financials: ['admin', 'sales', 'accountant'],
  },

  products: {
    view: ['admin', 'sales', 'deliverer', 'accountant'],
    view_all: ['admin', 'sales', 'deliverer', 'accountant'],
    create: ['admin', 'sales'],
    edit: ['admin', 'sales'],
    delete: ['admin'],
    manage: ['admin', 'sales'],
  },

  product_items: {
    view: ['admin', 'sales', 'deliverer', 'accountant'],
    view_all: ['admin', 'sales', 'deliverer', 'accountant'],
    create: ['admin', 'sales'],
    edit: ['admin', 'sales'],
    delete: ['admin'],
  },

  sales: {
    view: ['admin', 'sales', 'deliverer', 'accountant'], // Backend scopes by assignment for deliverer
    view_all: ['admin', 'sales', 'accountant'],
    create: ['admin', 'sales'],
    edit: ['admin', 'sales'],
    delete: ['admin', 'sales'],
    manage: ['admin', 'sales'],
  },

  sale_runner_ups: {
    view: ['admin', 'sales'], // Sensitive sales intelligence data
    view_all: ['admin', 'sales'],
    create: ['admin', 'sales'],
    edit: ['admin', 'sales'],
    delete: ['admin', 'sales'],
    manage: ['admin', 'sales'],
  },

  orders: {
    view: ['admin', 'sales', 'deliverer', 'accountant'], // Backend scopes by assignment for deliverer
    view_all: ['admin', 'sales', 'accountant'],
    create: ['admin', 'sales'],
    edit: ['admin', 'sales'],
    delete: ['admin', 'sales'],
    confirm: ['admin', 'sales', 'deliverer'], // Deliverer can confirm assigned orders
  },

  deliveries: {
    view: ['admin', 'sales', 'deliverer', 'accountant'], // Backend scopes by assignment for deliverer
    view_all: ['admin', 'sales', 'accountant'],
    create: ['admin', 'sales'],
    edit: ['admin', 'sales'],
    delete: ['admin', 'sales'],
    assign: ['admin', 'sales'],
    manage: ['admin', 'sales'],
  },

  payments: {
    view: ['admin', 'sales', 'accountant'], // Deliverers cannot access payments
    view_all: ['admin', 'sales', 'accountant'],
    create: ['admin', 'sales'],
    edit: ['admin', 'sales'],
    delete: ['admin', 'sales'],
    confirm: ['admin', 'sales'],
    allocate: ['admin', 'sales'],
    view_financials: ['admin', 'accountant'],
  },

  arrivals: {
    view: ['admin', 'sales', 'accountant'], // Deliverers cannot access arrivals
    view_all: ['admin', 'sales', 'accountant'],
    create: ['admin', 'sales'],
    edit: ['admin', 'sales'], // Only for draft arrivals
    delete: ['admin'],
    receive: ['admin', 'sales'],
    verify: ['admin', 'sales'],
    close: ['admin'], // Only admin can close
  },

  collaborators: {
    view: ['admin', 'sales', 'accountant'], // Deliverers cannot access
    view_all: ['admin', 'sales', 'accountant'],
    create: ['admin', 'sales'],
    edit: ['admin', 'sales'],
    delete: ['admin'],
    manage: ['admin', 'sales'],
  },

  collaborations: {
    view: ['admin', 'sales', 'accountant'],
    view_all: ['admin', 'sales', 'accountant'],
    create: ['admin', 'sales'],
    edit: ['admin', 'sales'],
    delete: ['admin'],
  },

  shippers: {
    view: ['admin', 'sales', 'deliverer', 'accountant'],
    view_all: ['admin', 'sales', 'deliverer', 'accountant'],
    create: ['admin', 'sales'],
    edit: ['admin', 'sales'],
    delete: ['admin'],
  },

  shipments: {
    view: ['admin', 'sales', 'deliverer', 'accountant'],
    view_all: ['admin', 'sales', 'deliverer', 'accountant'],
    create: ['admin', 'sales'],
    edit: ['admin', 'sales', 'deliverer'], // Deliverer can update status
    delete: ['admin'],
  },

  delivery_zones: {
    view: ['admin', 'sales', 'deliverer', 'accountant'],
    view_all: ['admin', 'sales', 'deliverer', 'accountant'],
    create: ['admin', 'sales'],
    edit: ['admin', 'sales'],
    delete: ['admin'],
  },

  locations: {
    view: ['admin', 'sales', 'deliverer', 'accountant'],
    view_all: ['admin', 'sales', 'accountant'],
    create: ['admin', 'sales'],
    edit: ['admin', 'sales'],
    delete: ['admin'],
  },

  analytics: {
    view: ['admin', 'sales', 'deliverer', 'accountant'],
    view_financials: ['admin', 'accountant'],
    refresh_cache: ['admin'],
  },

  settings: {
    view: ['admin', 'sales'], // Others can only view their own settings
    edit: ['admin', 'sales'],
    manage: ['admin'],
  },

  users: {
    view: ['admin', 'sales'], // Can list users (non-deliverer, non-accountant)
    view_all: ['admin'], // Admin can see all including other admins
    create: ['admin', 'sales'],
    edit: ['admin', 'sales'], // Can edit users in same org
    delete: ['admin'], // Only admin can delete
    manage: ['admin'],
  },
}

/**
 * Permission denial messages for better UX
 */
export const PERMISSION_MESSAGES: Record<string, string> = {
  'customers.adjust_balance': 'Only administrators can adjust customer balances',
  'customers.delete': 'Only administrators can delete customers',
  'products.delete': 'Only administrators can delete products',
  'sales.create': 'Only admin and sales users can create sales',
  'sales.edit': 'Only admin and sales users can edit sales',
  'sale_runner_ups.view': 'Runner-ups contain sensitive sales data - admin/sales only',
  'payments.view': 'Payment access is restricted to admin, sales, and accountant roles',
  'payments.create': 'Only admin and sales users can create payments',
  'arrivals.view': 'Arrival access is restricted to admin, sales, and accountant roles',
  'arrivals.create': 'Only admin and sales users can create arrivals',
  'arrivals.close': 'Only administrators can close arrivals',
  'collaborators.view': 'Collaborator access is restricted to admin, sales, and accountant roles',
  'deliveries.assign': 'Only admin and sales users can assign deliveries',
  'users.create': 'Only admin and sales users can create new users',
  'users.delete': 'Only administrators can delete users',
  'settings.edit': 'Only admin and sales users can modify system settings',
  'analytics.refresh_cache': 'Only administrators can refresh the analytics cache',

  // Generic fallbacks
  insufficient_permissions: 'You do not have permission to perform this action',
  'role.admin_only': 'This action is restricted to administrators only',
  'role.admin_or_sales': 'This action requires admin or sales role',
  'role.no_deliverer': 'Deliverers cannot perform this action',
}

/**
 * Get a user-friendly permission denial message
 */
export function getPermissionMessage(action: ActionType, resource: ResourceType): string {
  const key = `${resource}.${action}`
  return PERMISSION_MESSAGES[key] || PERMISSION_MESSAGES.insufficient_permissions
}

/**
 * Check if a role has permission for an action on a resource
 */
export function hasPermission(
  userRole: UserRole | undefined,
  action: ActionType,
  resource: ResourceType,
): boolean {
  if (!userRole) return false

  const resourcePermissions = PERMISSION_MATRIX[resource]
  if (!resourcePermissions) return false

  const allowedRoles = resourcePermissions[action]
  if (!allowedRoles) return false

  return allowedRoles.includes(userRole)
}

/**
 * Check if user has ANY of the specified roles
 */
export function hasAnyRole(userRole: UserRole | undefined, roles: UserRole[]): boolean {
  if (!userRole) return false
  return roles.includes(userRole)
}

/**
 * Check if user has ALL of the specified roles (rare, but included for completeness)
 */
export function hasAllRoles(userRole: UserRole | undefined, roles: UserRole[]): boolean {
  if (!userRole) return false
  // For a single role, user must be that exact role
  return roles.length === 1 && roles[0] === userRole
}
